import { useState, useEffect, useRef } from 'react';
import { Card, Table, Empty, Spin, Alert, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useSeason } from '../../hooks/useSeason';
import { useEvent } from '../../hooks/useEvent';
import { useTheme } from '../../contexts/useTheme';
import messagesService, { type EventMessage } from '../../api/messagesService';
import { getEventSource } from '../../utils/eventSource';

export const MessagesPage = () => {
  const { selectedSeason } = useSeason();
  const { selectedEvent } = useEvent();
  const { theme } = useTheme();
  const [messages, setMessages] = useState<EventMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track the last fetched event to prevent duplicate fetches
  const lastEventKeyRef = useRef<string>('');
  const fetchingRef = useRef(false);

  useEffect(() => {
    if (!selectedEvent) {
      setMessages([]);
      lastEventKeyRef.current = '';
      return;
    }

    // Create unique key for current event
    const eventKey = `${selectedEvent.season_year}-${selectedEvent.circuit_id}-${selectedEvent.start_date}`;

    // Skip if we're already fetching this event
    if (fetchingRef.current && lastEventKeyRef.current === eventKey) {
      return;
    }

    // Skip if event hasn't changed
    if (lastEventKeyRef.current === eventKey) {
      return;
    }

    lastEventKeyRef.current = eventKey;

    const fetchMessages = async () => {
      fetchingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        // Determine source based on event type
        const source = getEventSource(selectedEvent);

        const response = await messagesService.getEventMessages(
          selectedEvent.season_year,
          selectedEvent.circuit_id,
          selectedEvent.start_date,
          source
        );

        if (response.success && response.data) {
          setMessages(response.data);
        } else {
          setError('Failed to load messages');
        }
      } catch (err) {
        console.error('Error fetching messages:', err);
        setError(err instanceof Error ? err.message : 'Failed to load messages');
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    };

    fetchMessages();
  }, [selectedEvent]);

  const exportToCSV = () => {
    if (messages.length === 0) return;

    const headers = ['Time', 'Message', 'Championship'];
    const rows = messages.map(msg => [
      messagesService.formatTimestamp(msg.value_date, circuitTimeZone),
      msg.message,
      msg.championship || '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `messages-${selectedEvent?.circuit_id}-${selectedSeason?.year}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Group messages by championship
  const groupedMessages = messagesService.groupMessagesByChampionship(messages);
  // Sort: General first, then alphabetically
  const championships = Object.keys(groupedMessages).sort((a, b) => {
    if (a === 'General') return -1;
    if (b === 'General') return 1;
    return a.localeCompare(b);
  });

  // Get circuit timezone from selected event
  const circuitTimeZone = selectedEvent?.circuit?.time_zone;

  const columns = [
    {
      title: 'Time',
      dataIndex: 'value_date',
      key: 'value_date',
      width: 200,
      render: (timestamp: string) => messagesService.formatTimestamp(timestamp, circuitTimeZone),
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      render: (text: string) => (
        <span style={{ fontWeight: 500 }}>{text}</span>
      ),
    },
  ];

  if (!selectedEvent) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Scrollable Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          <Card>
            <Empty
              description={
                <span>
                  Please select a season and event to view messages
                </span>
              }
            />
          </Card>
        </div>
      </div>
    );
  }

  // Messages are only for legacy events - non-legacy events should use Alerts
  if (!selectedEvent.legacy) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          <Card>
            <Empty
              description={
                <span>
                  Messages are not available for non-legacy events.
                  <br />
                  Please use the Alerts page for event notifications.
                </span>
              }
            />
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        padding: '8px 8px 4px 8px',
        flexShrink: 0,
      }}>
        <Card>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', flex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {circuitTimeZone && (
                  <span style={{ 
                    fontSize: '12px', 
                    color: theme === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
                    fontStyle: 'italic'
                  }}>
                    Times displayed in circuit local time ({circuitTimeZone})
                  </span>
                )}               
              </div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {messages.length > 0 && (
                <Button
                  icon={<DownloadOutlined />}
                  onClick={exportToCSV}
                >
                  Export CSV
                </Button>
              )}
            </div>
          </div>

          {error && (
            <Alert
              title="Error"
              description={error}
              type="error"
              closable={{ onClose: () => setError(null) }}
              style={{ marginTop: 16 }}
            />
          )}
        </Card>
      </div>

      {/* Scrollable Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 8px 8px' }}>
        {loading ? (
          <Card style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </Card>
        ) : messages.length === 0 ? (
          <Card>
            <Empty description="No messages found for this event" />
          </Card>
        ) : (
          <>
            {championships.map((championship, index) => (
              <Card
                key={championship}
                title={championship}
                style={{ marginBottom: index < championships.length - 1 ? 12 : 0 }}
              >
                <Table
                  dataSource={groupedMessages[championship]}
                  columns={columns}
                  rowKey={(record) => `${record.season_year}-${record.circuit_id}-${record.start_date}-${record.value_date}-${record.message}`}
                  pagination={false}
                  size="middle"
                />
              </Card>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

